import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { blogService } from '../../../../services/blogService';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';

const AdminBlogs = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const data = await blogService?.getAllBlogs();
      setBlogs(data);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;

    try {
      await blogService?.deleteBlog(id);
      setBlogs(blogs?.filter(blog => blog?.id !== id));
    } catch (err) {
      console.error('Error deleting blog:', err);
      alert('Failed to delete blog post');
    }
  };

  const handleSendNewsletter = async (blog) => {
    if (!window.confirm(`Send newsletter for "${blog?.title}" to all subscribers?`)) return;

    try {
      setLoading(true);
      const result = await blogService?.sendNewsletterEmail(blog?.id);
      alert(`Newsletter sent successfully! ${result?.stats?.successful} emails sent.`);
    } catch (err) {
      console.error('Error sending newsletter:', err);
      alert(err?.message || 'Failed to send newsletter');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/admin/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <>
      <Helmet>
        <title>Manage Blogs - Admin Dashboard</title>
      </Helmet>
      <div className="min-h-screen bg-muted/30">
        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link to="/admin/dashboard">
                  <Button variant="ghost" size="sm" iconName="ArrowLeft" />
                </Link>
                <h1 className="text-lg md:text-xl font-bold text-foreground">Manage Blogs</h1>
              </div>
              <div className="flex items-center gap-2">
                <Link to="/">
                  <Button variant="outline" size="sm" iconName="Home" />
                </Link>
                <Button variant="ghost" size="sm" iconName="LogOut" onClick={handleSignOut} />
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Blog Posts</h2>
              <p className="text-muted-foreground">Manage all your blog posts</p>
            </div>
            <Link to="/admin/dashboard/blogs/new">
              <Button variant="default" iconName="Plus" iconPosition="left">
                New Blog Post
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading blogs...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground">{error}</p>
            </div>
          ) : blogs?.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border border-border">
              <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No blog posts yet</p>
              <Link to="/admin/dashboard/blogs/new">
                <Button variant="default" iconName="Plus" iconPosition="left">
                  Create Your First Blog Post
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {blogs?.map((blog) => (
                <div
                  key={blog?.id}
                  className="bg-card rounded-xl border border-border p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-semibold text-foreground mb-1">
                            {blog?.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                            <span className={`px-2 py-1 rounded-md text-xs ${
                              blog?.status === 'published' ? 'bg-green-500/10 text-green-600' :
                              blog?.status === 'draft'? 'bg-yellow-500/10 text-yellow-600' : 'bg-gray-500/10 text-gray-600'
                            }`}>
                              {blog?.status}
                            </span>
                            {blog?.scheduledPublishDate && blog?.status === 'draft' && (
                              <span className="px-2 py-1 bg-blue-500/10 text-blue-600 rounded-md text-xs">
                                Scheduled: {formatDate(blog?.scheduledPublishDate)}
                              </span>
                            )}
                            <span>•</span>
                            <span>{blog?.category}</span>
                            <span>•</span>
                            <span>{formatDate(blog?.publishedDate || blog?.createdAt)}</span>
                            <span>•</span>
                            <span>{blog?.readTime} min read</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                        {blog?.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Link to={`/admin/dashboard/blogs/edit/${blog?.id}`}>
                          <Button variant="outline" size="sm" iconName="Edit" iconPosition="left">
                            Edit
                          </Button>
                        </Link>
                        {blog?.status === 'published' && (
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Mail"
                            iconPosition="left"
                            onClick={() => handleSendNewsletter(blog)}
                            disabled={loading}
                          >
                            Send Newsletter
                          </Button>
                        )}
                        <Link to={`/blog-posts/${blog?.id}`} target="_blank">
                          <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="left">
                            View
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          iconPosition="left"
                          onClick={() => handleDelete(blog?.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default AdminBlogs;
